import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import s from './ui.module.css'

type ToastTone = 'neutral' | 'success' | 'danger' | 'warning'

interface Toast {
  id: number
  tone: ToastTone
  message: ReactNode
  /* A trace id turns a support ticket into one log search, so error toasts
     carry it — small, copyable, non-alarming. */
  traceId?: string | undefined
}

interface ToastApi {
  show: (message: ReactNode, options?: { tone?: ToastTone; traceId?: string; ms?: number }) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const tones: Record<ToastTone, string> = {
  neutral: '',
  success: s.toastSuccess!,
  danger: s.toastDanger!,
  warning: s.toastWarning!,
}

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
    const timer = timers.current.get(id)
    if (timer !== undefined) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const show = useCallback<ToastApi['show']>(
    (message, options) => {
      const id = nextId.current++
      const toast: Toast = {
        id,
        tone: options?.tone ?? 'neutral',
        message,
        traceId: options?.traceId,
      }
      setToasts((current) => [...current, toast])
      // Errors linger; confirmations do not.
      const ms = options?.ms ?? (toast.tone === 'danger' ? 8000 : 4000)
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), ms),
      )
    },
    [dismiss],
  )

  useEffect(() => {
    const pending = timers.current
    return () => {
      for (const timer of pending.values()) clearTimeout(timer)
      pending.clear()
    }
  }, [])

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className={s.toastRegion} role="region" aria-label="Notifications">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={[s.toast, tones[toast.tone]].filter(Boolean).join(' ')}
              role={toast.tone === 'danger' ? 'alert' : 'status'}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div>{toast.message}</div>
                {toast.traceId !== undefined && (
                  <div style={{ opacity: 0.85, fontSize: 'var(--fs-xs)', marginTop: 'var(--s-1)' }}>
                    Reference: <code>{toast.traceId}</code>
                  </div>
                )}
              </div>
              <button
                type="button"
                className={s.toastClose}
                onClick={() => dismiss(toast.id)}
                aria-label="Dismiss notification"
              >
                ×
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext)
  if (context === null) throw new Error('useToast must be used inside a ToastProvider')
  return context
}
